import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapProvider } from '../map/map-provider';
import { MousePositionState } from './mouse-position-state';
import { MouseProjectionEnum } from '@gishub/domain';
import { ProjectionService, UtmCoords } from '../../../../shared/services/projection-service';
import { SpatialReferenceRepositoryProvider } from '../../../../shared/providers/repository-providers';
import Point from '@arcgis/core/geometry/Point';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';

@Injectable()
export class MousePositionNotifier extends BaseNotifier<MousePositionState> {
  private static readonly INITIAL: MousePositionState = {
    wgs84: null,
    utm17s: null,
    utm18s: null,
    utm19s: null,
    projection: MouseProjectionEnum.Wgs84,
    currentScale: null,
    projections: [],
  };

  private handle: { remove(): void } | null = null;
  private scaleHandle: { remove(): void } | null = null;

  constructor(ref: Ref) {
    super(ref, MousePositionNotifier.INITIAL);
  }

  override build(): void {
    const projectionSvc = this.ref.read(ProjectionService);
    this.updateState(s => ({
      ...s,
      projections: this.ref.read(SpatialReferenceRepositoryProvider).instance.getAll(),
    }));

    // Limpia los handles activos del view al destruir el notifier.
    this.trackDisposable(() => {
      this.scaleHandle?.remove();
      this.scaleHandle = null;
      this.handle?.remove();
      this.handle = null;
    });

    this.listen(() => {
      const v = this.ref.watch(MapProvider).state().view;

      if (this.scaleHandle) {
        this.scaleHandle.remove();
        this.scaleHandle = null;
      }
      if (this.handle) {
        this.handle.remove();
        this.handle = null;
      }

      if (!v) return;

      const updateScale = () => {
        this.updateState(s => ({ ...s, currentScale: v.scale }));
      };
      updateScale();
      this.scaleHandle = reactiveUtils.watch(() => v.scale, () => updateScale());

      this.handle = v.on('pointer-move', (event: any) => {
        const point = v.toMap({ x: event.x, y: event.y }) as Point;
        if (!point) return;
        const { latitude, longitude } = point;
        if (latitude == null || longitude == null) return;

        this.updateState(s => ({
          ...s,
          wgs84: projectionSvc.toWgs84(latitude, longitude),
          utm17s: projectionSvc.toUtm(latitude, longitude, '17S'),
          utm18s: projectionSvc.toUtm(latitude, longitude, '18S'),
          utm19s: projectionSvc.toUtm(latitude, longitude, '19S'),
        }));
      });
    });
  }

  setProjection(projection: MouseProjectionEnum): void {
    this.setState({ ...this.snapshot(), projection });
  }

  /**
   * Devuelve la coordenada UTM de un punto geográfico WGS84 usando la
   * proyección seleccionada por el usuario (si es una zona UTM); si no hay
   * una zona UTM seleccionada, usa la primera zona por defecto (17S).
   */
  utmFor(lat: number, lon: number): UtmCoords | null {
    const projectionSvc = this.ref.read(ProjectionService);
    const all = projectionSvc.getAllUtm(lat, lon);
    const selected = this.snapshot().projection;
    let zone: string;
    switch (selected) {
      case MouseProjectionEnum.Utm18S:
        zone = '18S';
        break;
      case MouseProjectionEnum.Utm19S:
        zone = '19S';
        break;
      default:
        zone = '17S';
        break;
    }
    return all.find(u => u.zone === zone) ?? null;
  }

  goToScale(scale: number): void {
    const v = this.ref.watch(MapProvider).state().view;
    if (v) {
      v.scale = scale;
    }
  }
}
